import React, {useEffect, useMemo, useState} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
  CssBaseline,
} from "@mui/material";
import {ThemeProvider, createTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useLoginMutation} from "../../app/services/auth";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {ALLOWED_PARENT_ORIGINS} from "../../util/helpers";
import {LoginResolutionPolicy} from "../../constants/enums";
import {getIdentityErrorMessage} from "../../util/identityErrorMapper";

export const GenericEmailLogin: React.FC = () => {
  const {t} = useTranslation();
  // Dark mode functionality commented out
  // const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  // const [themeMode, setThemeMode] = useState<"light" | "dark" | null>(null);

  // const theme = useMemo(
  //   () =>
  //     createTheme({
  //       palette: {
  //         mode: themeMode ?? (prefersDarkMode ? "dark" : "light"),
  //       },
  //     }),
  //   [themeMode, prefersDarkMode],
  // );

  const [showPassword, setShowPassword] = useState(false);
  const [preLoginToken, setPreLoginToken] = useState<string>("");
  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const handleShowPasswordChange = () => setShowPassword(!showPassword);

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("email_login.validation.emailInvalid"))
      .required(t("email_login.validation.emailRequired")),
    password: Yup.string().required(
      t("email_login.validation.passwordRequired"),
    ),
  });

  // Login Mutation
  const [login, {isLoading}] = useLoginMutation();

  //* Validate hostOrigin from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const originParam = params.get("hostOrigin");

    if (originParam && ALLOWED_PARENT_ORIGINS.includes(originParam)) {
      setHostOrigin(originParam);
    } else {
      console.error("Untrusted hostOrigin:", originParam);
    }
  }, []);

  //* Listen for message (PreLoginToken) from Client Apps
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (hostOrigin) {
        const allowedOrigin = new URL(hostOrigin).origin;

        // Only accept messages from the parent (ClientApp), not self (IDMS)
        if (event.origin !== allowedOrigin) {
          console.debug(
            "Ignored message from unexpected origin:",
            event.origin,
          );
          return;
        }
      }

      if (event.data?.type === "PRE_LOGIN_TOKEN") {
        const token = event.data.token;
        setPreLoginToken(token);
      }

      // Dark mode functionality commented out
      // if (event.data?.type === "THEME_PREFERENCE") {
      //   const mode = event.data.mode;
      //   if (mode === "light" || mode === "dark") {
      //     setThemeMode(mode);
      //   }
      // }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin]);

  // Child: request preLoginToken from parent
  useEffect(() => {
    if (hostOrigin) {
      window.parent.postMessage({type: "REQUEST_PRE_LOGIN_TOKEN"}, hostOrigin);
    }
  }, [hostOrigin]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const loginReqest: GenericEmailLoginRequest = {
        cacheKey: preLoginToken,
        username: values.email,
        password: values.password,
        loginResolutionPolicy: LoginResolutionPolicy.CacheKeyEmail,
      };

      await login(loginReqest)
        .unwrap()
        .then(response => {
          toast.success(t("email_login.messages.loginSuccess"));

          // Send token to host page (Client App)
          if (hostOrigin) {
            window.parent.postMessage(
              {
                type: "LOGIN_SUCCESS",
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                expires_in: response.expires_in,
                token_type: response.token_type,
                scope: response.scope,
              },
              hostOrigin,
            );
          }
        })
        .catch(error => {
          console.error("Login failed", error);
          toast.error(getIdentityErrorMessage(error, t));
        });
    },
  });

  // Remember me checkbox handler
  const handleCheckboxChange = (isCheck: boolean) => {
    if (isCheck) {
      if (formik.values.email.length > 0) {
        window.localStorage.setItem(
          "X_EmailLogin_RememberMe_info_X",
          JSON.stringify({isCheck: true, email: formik.values.email}),
        );
        setRememberMe(true);
      } else {
        window.localStorage.removeItem("X_EmailLogin_RememberMe_info_X");
        setRememberMe(false);
      }
    } else {
      window.localStorage.removeItem("X_EmailLogin_RememberMe_info_X");
      setRememberMe(false);
    }
  };

  // On mount, load remembered email if present
  useEffect(() => {
    const loginInfo = window.localStorage.getItem(
      "X_EmailLogin_RememberMe_info_X",
    );
    if (loginInfo != null) {
      const values = JSON.parse(loginInfo);
      if (values.isCheck) {
        setRememberMe(true);
        formik.setFieldValue("email", values.email);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When email changes and rememberMe is checked, update localStorage
  useEffect(() => {
    if (rememberMe) {
      if (formik.values.email.length > 0) {
        window.localStorage.setItem(
          "X_EmailLogin_RememberMe_info_X",
          JSON.stringify({isCheck: true, email: formik.values.email}),
        );
      } else {
        window.localStorage.removeItem("X_EmailLogin_RememberMe_info_X");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.email]);

  return (
    // Dark mode functionality commented out
    // <ThemeProvider theme={theme}>
    //   <CssBaseline />
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "background.paper",
            padding: 4,
            borderRadius: 2,
            boxShadow: 3,
          }}>
          <Avatar sx={{bgcolor: "primary.main"}}>
            <LockOutlinedIcon />
          </Avatar>

          <Typography component="h1" variant="h5" sx={{mb: 3}}>
            {t("email_login.title")}
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              label={t("email_login.emailLabel")}
              name="email"
              autoComplete="email"
              autoFocus
              value={formik.values.email}
              onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />

            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              name="password"
              label={t("email_login.passwordLabel")}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="passwordVisibilityBtn"
                      onClick={handleShowPasswordChange}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              style={{display: "flex"}}
              label="Remember me"
              control={
                <Checkbox
                  size="medium"
                  color="primary"
                  checked={rememberMe}
                  onChange={e => handleCheckboxChange(e.target.checked)}
                />
              }
            />

            {/* <Box sx={{width: "100%", textAlign: "right", mt: 1}}>
              <Link href="#" variant="body2">
                {t("email_login.forgotPassword")}
              </Link>
            </Box> */}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{my: 2}}
              disabled={isLoading}>
              {t("email_login.loginButton")}
              {isLoading && (
                <CircularProgress
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: -12,
                    marginLeft: -12,
                    color: "secondary",
                  }}
                  size={24}
                />
              )}
            </Button>
          </form>

          <Link
            href="#"
            variant="body2"
            onClick={e => {
              e.preventDefault();
              if (hostOrigin) {
                window.parent.postMessage("USE_CLASSIC_LOGIN", hostOrigin);
              }
            }}>
            {t("email_login.classicLogin")}
          </Link>
        </Box>
      </Container>
    // </ThemeProvider>
  );
};
